import React from 'react';
import { createPortal } from 'react-dom';

export type ProgramProjectTimetableRenameRow = {
  from: string;
  to: string;
};

type Props = {
  open: boolean;
  initialNames: readonly string[];
  busy?: boolean;
  onClose: () => void;
  onConfirm: (replacements: ProgramProjectTimetableRenameRow[]) => void;
};

const ProgramProjectTimetableRenameModal: React.FC<Props> = ({
  open,
  initialNames,
  busy = false,
  onClose,
  onConfirm,
}) => {
  const [rows, setRows] = React.useState<ProgramProjectTimetableRenameRow[]>([]);

  React.useEffect(() => {
    if (!open) return;
    setRows(initialNames.map((from) => ({ from, to: '' })));
  }, [open, initialNames]);

  if (!open) return null;

  return createPortal(
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onMouseDown={(ev) => {
        if (ev.target === ev.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="cp-modal cp-program-project-timetable-rename-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-program-project-timetable-rename-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <h3 id="cp-program-project-timetable-rename-title">시간표 이름 바꾸기</h3>
        <div className="cp-modal-body">
          {rows.length === 0 ? (
            <p className="cp-program-project-timetable-rename-empty" role="status">
              표에 입력된 시간표명이 없습니다.
            </p>
          ) : (
            <>
              <div className="cp-program-project-timetable-rename-head" aria-hidden>
                <span>변경 전 시간표명</span>
                <span>변경 후 시간표명</span>
              </div>
              <div className="cp-program-project-timetable-rename-rows">
                {rows.map((row, idx) => (
                  <div key={`${row.from}::${idx}`} className="cp-program-project-timetable-rename-row">
                    <input
                      type="text"
                      className="cp-input"
                      value={row.from}
                      readOnly
                      aria-label={`변경 전 시간표명 ${idx + 1}`}
                    />
                    <input
                      type="text"
                      className="cp-input"
                      value={row.to}
                      disabled={busy}
                      placeholder="변경할 시간표명 입력"
                      aria-label={`변경 후 시간표명 ${idx + 1}`}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRows((prev) =>
                          prev.map((r, i) => (i === idx ? { ...r, to: value } : r))
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="cp-modal-footer">
          <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={busy}>
            취소
          </button>
          <button
            type="button"
            className="cp-btn-primary"
            disabled={busy || rows.length === 0}
            onClick={() => {
              const replacements = rows.filter((r) => r.to.trim().length > 0);
              onConfirm(replacements);
            }}
          >
            변경
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProgramProjectTimetableRenameModal;
